import { BookOpen, CalendarDays, ChefHat, ChevronsUpDown, Ellipsis, LogOut, Refrigerator, Settings, ShoppingBasket } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthProvider";
import { BrandMark } from "../components/BrandMark";
import { Button } from "../components/Button";
import { Dialog } from "../components/Dialog";
import { IconButton } from "../components/IconButton";
import { OfflineBanner } from "../components/Offline";
import { effectiveStatus, formatClock, remainingSeconds, TimerWatcher, useNow } from "../features/timers";
import { useCookSessions, useTimers } from "../lib/queries";

const NAV = [
  { to: "/recipes", label: "Recipes", icon: BookOpen },
  { to: "/plan", label: "Plan", icon: CalendarDays },
  { to: "/groceries", label: "Groceries", icon: ShoppingBasket },
  { to: "/pantry", label: "Pantry", icon: Refrigerator },
] as const;

function initials(text: string) {
  const parts = text.trim().split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "S") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Household-scoped layout shared by the app shell and cook mode (timers ring everywhere). */
export function HouseholdLayout() {
  return (
    <>
      <TimerWatcher />
      <Outlet />
    </>
  );
}

export function HouseholdSwitcherDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { me, householdId, selectHousehold } = useAuth();
  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Switch household" description="Each household keeps its own recipes, lists, pantry and plans." size="sm">
      <ul className="switcher-list">
        {(me?.households ?? []).map((h) => (
          <li key={h.id}>
            <button
              type="button"
              className={`switcher-item ${h.id === householdId ? "is-current" : ""}`}
              aria-current={h.id === householdId ? "true" : undefined}
              onClick={async () => {
                if (h.id !== householdId) {
                  await selectHousehold(h.id);
                  toast.success(`Switched to ${h.name}`, { id: "household-switch" });
                  navigate("/recipes");
                }
                onOpenChange(false);
              }}
            >
              <span className="switcher-item__name">{h.name}</span>
              <span className="switcher-item__meta">
                {h.role} · {h.memberCount} {h.memberCount === 1 ? "member" : "members"}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <Link to="/welcome?add=1" className="text-link" onClick={() => onOpenChange(false)}>
        Create or join another household
      </Link>
    </Dialog>
  );
}

function CookingStrip() {
  const sessions = useCookSessions();
  const timers = useTimers();
  const pinned = (sessions.data ?? []).filter((s) => s.pinned);
  const running = (timers.data ?? []).filter((t) => t.status !== "paused");
  const now = useNow(running.length > 0);
  if (!pinned.length) return null;
  return (
    <nav className="cooking-strip" aria-label="Recipes you're cooking">
      <ChefHat size={18} aria-hidden="true" />
      <span className="cooking-strip__label">Cooking</span>
      {pinned.map((s) => {
        const next = (timers.data ?? [])
          .filter((t) => t.recipeId === s.recipeId && t.status !== "paused")
          .sort((a, b) => remainingSeconds(a, now) - remainingSeconds(b, now))[0];
        const done = next ? effectiveStatus(next, now) === "done" : false;
        return (
          <Link key={s.recipeId} to={`/cook/${s.recipeId}`} className="cooking-strip__link">
            <span>{s.recipeTitle}</span>
            {next ? <span className={`cooking-strip__timer ${done ? "is-done" : ""}`}>{done ? "Done" : formatClock(remainingSeconds(next, now))}</span> : null}
          </Link>
        );
      })}
    </nav>
  );
}

export function AppShell() {
  const { me, household, signOut } = useAuth();
  const location = useLocation();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const multiple = (me?.households.length ?? 0) > 1;

  // Move focus to the page heading on route change (not on first load) for screen readers.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const heading = mainRef.current?.querySelector<HTMLElement>("[data-page-title]");
    heading?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  const moreActive = location.pathname.startsWith("/settings") || location.pathname.startsWith("/more");
  const displayName = me?.user.displayName || me?.user.email || "";

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <aside className="sidebar" aria-label="Main">
        <Link to="/recipes" className="brand sidebar__brand">
          <BrandMark />
          <span className="brand__word">Supperkeep</span>
        </Link>
        <div>
          <p className="sidebar__section-label">Household</p>
          <button type="button" className="household-chip household-chip--wide" onClick={() => setSwitcherOpen(true)} aria-label={`Household: ${household?.name}. ${multiple ? "Switch household" : "Household options"}`}>
            <span>{household?.name}</span>
            <ChevronsUpDown size={16} aria-hidden="true" />
          </button>
        </div>
        <nav className="sidebar__nav" aria-label="Sections">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className="sidebar__link">
              <Icon aria-hidden="true" />
              {label}
            </NavLink>
          ))}
          <NavLink to="/cook" className="sidebar__link">
            <ChefHat aria-hidden="true" />
            Cooking
          </NavLink>
          <NavLink to="/settings" className="sidebar__link">
            <Settings aria-hidden="true" />
            Settings
          </NavLink>
        </nav>
        <div className="sidebar__footer">
          <div className="sidebar__account">
            <span className="avatar" aria-hidden="true">
              {initials(displayName)}
            </span>
            <span className="sidebar__account-text">
              <strong>{me?.user.displayName || "Signed in"}</strong>
              <span className="muted">{me?.user.email}</span>
            </span>
            <IconButton
              label="Sign out"
              icon={<LogOut />}
              busy={signingOut}
              onClick={async () => {
                setSigningOut(true);
                await signOut();
              }}
            />
          </div>
        </div>
      </aside>

      <div className="app__column">
        <header className="topbar">
          <Link to="/recipes" className="brand">
            <BrandMark />
            <span className="brand__word">Supperkeep</span>
          </Link>
          <button type="button" className="household-chip" onClick={() => setSwitcherOpen(true)} aria-label={`Household: ${household?.name}. ${multiple ? "Switch household" : "Household options"}`}>
            <span>{household?.name}</span>
            <ChevronsUpDown size={16} aria-hidden="true" />
          </button>
        </header>
        <OfflineBanner />
        <CookingStrip />
        <main id="main" className="page" ref={mainRef} tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      <nav className="bottom-nav" aria-label="Sections">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className="bottom-nav__item">
            <span className="bottom-nav__icon" aria-hidden="true">
              <Icon size={22} />
            </span>
            <span className="bottom-nav__label">{label}</span>
          </NavLink>
        ))}
        <NavLink to="/settings" className="bottom-nav__item" aria-current={moreActive ? "page" : undefined}>
          <span className="bottom-nav__icon" aria-hidden="true">
            <Ellipsis size={22} />
          </span>
          <span className="bottom-nav__label">More</span>
        </NavLink>
      </nav>

      <HouseholdSwitcherDialog open={switcherOpen} onOpenChange={setSwitcherOpen} />
    </div>
  );
}

export function SignOutButton() {
  const { signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      emphasis="outline"
      intent="neutral"
      icon={<LogOut />}
      busy={busy}
      onClick={async () => {
        setBusy(true);
        await signOut();
      }}
    >
      Sign out
    </Button>
  );
}
